import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import API from "@/lib/api";
import {
  Activity, Users, TrendingUp, Clock, Cpu, HardDrive,
  BarChart3, Server, RefreshCw, CheckCircle2
} from "lucide-react";
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer } from "recharts";
import { getErrorMessage } from "@/lib/errorHandler";

const formatDuration = (minutes) => {
  if (minutes == null) return '—';
  const totalMinutes = Math.round(minutes);
  if (totalMinutes < 60) return `${totalMinutes}m`;
  const days = Math.floor(totalMinutes / (60 * 24));
  const hours = Math.floor((totalMinutes % (60 * 24)) / 60);
  const mins = totalMinutes % 60;
  if (days > 0) return `${days}d ${hours}h ${mins}m`;
  return `${hours}h ${mins}m`;
};

export default function SystemMonitoring() {
  const [stats, setStats] = useState(null);
  const [sessions, setSessions] = useState([]);
  const [activeQuizTakers, setActiveQuizTakers] = useState([]);
  const [summary, setSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [autoRefresh, setAutoRefresh] = useState(true);
  const [refreshing, setRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [showActiveUsersModal, setShowActiveUsersModal] = useState(false);

  const fetchStats = async () => {
    try {
      console.log("Fetching monitoring stats...");
      const res = await API.get("/monitoring/dashboard");
      console.log("Stats response:", res.data);
      setStats(res.data);
      setError(null);
    } catch (err) {
      console.error("Failed to fetch stats:", err);
      console.error("Error details:", err.response?.data);
      setError(getErrorMessage(err, "Failed to load monitoring data"));
    }
  };

  const fetchSessions = async () => {
    try {
      const res = await API.get("/monitoring/active-sessions");
      console.log("Active sessions data:", res.data.active_sessions);
      setSessions(res.data.active_sessions || []);
    } catch (err) {
      console.error("Failed to fetch sessions:", err);
    }
  };

  const fetchActiveQuizTakers = async () => {
    try {
      const res = await API.get("/monitoring/active-quiz-takers");
      console.log("Active quiz takers data:", res.data.active_quiz_takers);
      setActiveQuizTakers(res.data.active_quiz_takers || []);
    } catch (err) {
      console.error("Failed to fetch active quiz takers:", err);
    }
  };

  const fetchSummary = async () => {
    try {
      const res = await API.get("/monitoring/stats/summary");
      setSummary(res.data);
    } catch (err) {
      console.error("Failed to fetch summary:", err);
    } finally {
      setLoading(false);
    }
  };

  const refreshAll = async () => {
    setRefreshing(true);
    await Promise.all([fetchStats(), fetchSessions(), fetchActiveQuizTakers(), fetchSummary()]);
    setRefreshing(false);
  };

  useEffect(() => {
    refreshAll();
  }, []);

  // Auto-refresh every 10 seconds
  useEffect(() => {
    if (!autoRefresh) return;

    const interval = setInterval(() => {
      fetchStats();
      fetchSessions();
      fetchActiveQuizTakers();
    }, 10000);

    return () => clearInterval(interval);
  }, [autoRefresh]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12">
        <RefreshCw className="w-6 h-6 animate-spin text-indigo-600" />
        <span className="ml-2 text-slate-600">Loading monitoring data...</span>
      </div>
    );
  }

  // Show error if API failed
  if (error) {
    return (
      <div className="p-6 max-w-7xl mx-auto">
        <div className="bg-red-50 border border-red-200 rounded-lg p-6">
          <div className="flex items-start gap-3">
            <div className="p-2 bg-red-100 rounded-lg">
              <Activity className="w-5 h-5 text-red-600" />
            </div>
            <div className="flex-1">
              <h3 className="text-lg font-semibold text-red-900 mb-2">Failed to Load Monitoring Data</h3>
              <p className="text-sm text-red-700 mb-4">{error}</p>
              <div className="space-y-2 text-sm text-red-600">
                <p><strong>Common causes:</strong></p>
                <ul className="list-disc list-inside space-y-1 ml-2">
                  <li>You don't have the "can_view_system_stats" permission</li>
                  <li>Backend API is not running</li>
                  <li>Network/CORS issue</li>
                </ul>
              </div>
              <Button
                onClick={refreshAll}
                className="mt-4 bg-red-600 hover:bg-red-700"
                size="sm"
              >
                <RefreshCw className="w-4 h-4 mr-2" />
                Try Again
              </Button>
            </div>
          </div>
        </div>
        <div className="mt-4 p-4 bg-slate-50 border border-slate-200 rounded-lg">
          <p className="text-sm font-medium text-slate-900 mb-2">Debug Steps:</p>
          <ol className="text-sm text-slate-600 space-y-1 list-decimal list-inside">
            <li>Open browser console (F12) and check for errors</li>
            <li>Verify your admin account has the monitoring permission</li>
            <li>Check backend logs for errors</li>
          </ol>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6 animate-fade-in-up">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold text-slate-900">System Monitoring</h1>
          <p className="text-sm text-slate-500 mt-1">Real-time system statistics and performance metrics</p>
        </div>
        <div className="flex items-center gap-3">
          <div className="flex items-center gap-2">
            <input
              type="checkbox"
              id="auto-refresh"
              checked={autoRefresh}
              onChange={(e) => setAutoRefresh(e.target.checked)}
              className="rounded"
            />
            <label htmlFor="auto-refresh" className="text-sm text-slate-600">
              Auto-refresh (10s)
            </label>
          </div>
          <Button
            size="sm"
            variant="outline"
            onClick={refreshAll}
            disabled={refreshing}
          >
            <RefreshCw className={`w-4 h-4 mr-1.5 ${refreshing ? 'animate-spin' : ''}`} />
            Refresh
          </Button>
        </div>
      </div>

      {/* Real-time Stats Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard
          title="Active Users"
          value={stats?.realtime_stats?.active_quiz_takers || 0}
          subtitle="Taking assessments now"
          icon={<Users className="w-5 h-5" />}
          color="blue"
          onClick={() => setShowActiveUsersModal(true)}
          clickable={true}
        />
        <StatCard
          title="Active Sessions"
          value={stats?.realtime_stats?.active_sessions || 0}
          subtitle="Online users (15 min)"
          icon={<Activity className="w-5 h-5" />}
          color="green"
        />
        <StatCard
          title="Submissions Today"
          value={stats?.realtime_stats?.submissions_today || 0}
          subtitle={`${Math.round(stats?.realtime_stats?.completion_rate || 0)}% completed`}
          icon={<TrendingUp className="w-5 h-5" />}
          color="indigo"
        />
        <StatCard
          title="Avg Response Time"
          value={`${Math.round(stats?.performance_metrics?.avg_response_time_ms || 0)}ms`}
          subtitle={`${stats?.performance_metrics?.error_rate_percent || 0}% errors`}
          icon={<Clock className="w-5 h-5" />}
          color={stats?.performance_metrics?.avg_response_time_ms > 500 ? "red" : "green"}
        />
      </div>

      {/* System Resources */}
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <ResourceCard
          title="CPU Usage"
          value={stats?.system_resources?.cpu_percent || 0}
          icon={<Cpu className="w-5 h-5" />}
        />
        <ResourceCard
          title="Memory Usage"
          value={stats?.system_resources?.memory_percent || 0}
          subtitle={`${stats?.system_resources?.memory_used_gb}GB / ${stats?.system_resources?.memory_total_gb}GB`}
          icon={<Server className="w-5 h-5" />}
        />
        <ResourceCard
          title="Disk Usage"
          value={stats?.system_resources?.disk_percent || 0}
          subtitle={`${stats?.system_resources?.disk_used_gb}GB / ${stats?.system_resources?.disk_total_gb}GB`}
          icon={<HardDrive className="w-5 h-5" />}
        />
      </div>

      {/* Summary Statistics */}
      {summary && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <SummaryCard title="Total Quizzes" value={summary.totals.quizzes} />
          <SummaryCard title="Total Questions" value={summary.totals.questions} />
          <SummaryCard title="Total Submissions" value={summary.totals.submissions} />
          <SummaryCard title="Total Users" value={summary.totals.users} />
        </div>
      )}

      {/* Popular Quizzes Chart */}
      {stats?.analytics?.popular_quizzes?.length > 0 && (
        <Card>
          <CardHeader>
            <CardTitle className="text-base">Most Active Quizzes (7 days)</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={stats.analytics.popular_quizzes}>
                <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" />
                <XAxis
                  dataKey="title"
                  angle={-45}
                  textAnchor="end"
                  height={100}
                  tick={{ fontSize: 12 }}
                />
                <YAxis tick={{ fontSize: 12 }} />
                <Tooltip />
                <Bar dataKey="submissions" fill="#4F46E5" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      )}

      {/* Active Sessions Table */}
      <Card>
        <CardHeader>
          <div className="flex items-center justify-between">
            <CardTitle className="text-base">Active Sessions ({sessions.length})</CardTitle>
            <Badge variant="outline" className="text-xs">
              <Activity className="w-3 h-3 mr-1" />
              Live
            </Badge>
          </div>
        </CardHeader>
        <CardContent>
          {sessions.length === 0 ? (
            <div className="text-center py-8 text-slate-500">
              <Activity className="w-10 h-10 text-slate-300 mx-auto mb-2" />
              <p>No active sessions</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-slate-200">
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">User</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Type</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Duration</th>
                    <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Last Activity</th>
                  </tr>
                </thead>
                <tbody>
                  {sessions.map(session => (
                    <tr key={session.id} className="border-b border-slate-100 hover:bg-slate-50">
                      <td className="py-3 px-4 text-sm text-slate-900">
                        {session.user_email || "Anonymous"}
                      </td>
                      <td className="py-3 px-4">
                        <Badge
                          variant={session.user_type === "admin" ? "default" : "secondary"}
                          className="text-xs"
                        >
                          {session.user_type}
                        </Badge>
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-600">
                        {formatDuration(session.duration_minutes)}
                      </td>
                      <td className="py-3 px-4 text-sm text-slate-500">
                        {new Date(session.last_activity).toLocaleTimeString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Footer Info */}
      <div className="text-center text-xs text-slate-400">
        Last updated: {stats?.timestamp ? new Date(stats.timestamp).toLocaleTimeString() : '—'}
      </div>

      {/* Active Users Modal */}
      <Dialog open={showActiveUsersModal} onOpenChange={setShowActiveUsersModal}>
        <DialogContent className="max-w-4xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Users className="w-5 h-5 text-blue-600" />
              Active Users - Currently Taking Assessments
            </DialogTitle>
          </DialogHeader>
          <div className="mt-4">
            {activeQuizTakers.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <Users className="w-16 h-16 text-slate-300 mx-auto mb-3" />
                <p className="text-lg font-medium">No active users</p>
                <p className="text-sm mt-1">No one is currently taking assessments</p>
              </div>
            ) : (
              <div className="space-y-4">
                <div className="flex items-center justify-between pb-3 border-b">
                  <p className="text-sm text-slate-600">
                    <span className="font-semibold text-slate-900">
                      {activeQuizTakers.length}
                    </span> {activeQuizTakers.length === 1 ? 'user' : 'users'} currently taking assessments
                  </p>
                  <Badge variant="outline" className="text-xs">
                    <Activity className="w-3 h-3 mr-1" />
                    Live
                  </Badge>
                </div>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead>
                      <tr className="border-b border-slate-200">
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">User</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Quiz</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Duration</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Started</th>
                        <th className="text-left py-3 px-4 text-xs font-medium text-slate-600">Status</th>
                      </tr>
                    </thead>
                    <tbody>
                      {activeQuizTakers.map(taker => (
                        <tr key={taker.id} className="border-b border-slate-100 hover:bg-slate-50">
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <div className="w-8 h-8 rounded-full bg-blue-100 flex items-center justify-center text-blue-700 text-xs font-semibold">
                                {taker.user_email?.charAt(0)?.toUpperCase() || 'U'}
                              </div>
                              <span className="text-sm text-slate-900">
                                {taker.user_email || 'Anonymous'}
                              </span>
                            </div>
                          </td>
                          <td className="py-3 px-4">
                            <div className="max-w-xs">
                              <p className="text-sm text-slate-900 truncate" title={taker.quiz_title}>
                                {taker.quiz_title}
                              </p>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-600">
                            {formatDuration(taker.duration_minutes)}
                          </td>
                          <td className="py-3 px-4 text-sm text-slate-500">
                            {new Date(taker.started_at).toLocaleTimeString()}
                          </td>
                          <td className="py-3 px-4">
                            <Badge variant="secondary" className="text-xs bg-green-50 text-green-700 border-green-200">
                              <Activity className="w-3 h-3 mr-1" />
                              In Progress
                            </Badge>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}

// Stat Card Component
function StatCard({ title, value, subtitle, icon, color, onClick, clickable }) {
  const colors = {
    blue: "bg-blue-50 text-blue-600",
    green: "bg-green-50 text-green-600",
    indigo: "bg-indigo-50 text-indigo-600",
    red: "bg-red-50 text-red-600"
  };

  return (
    <Card
      className={`border-slate-200 shadow-sm ${clickable ? 'cursor-pointer hover:shadow-md hover:border-slate-300 transition-all' : ''}`}
      onClick={clickable ? onClick : undefined}
    >
      <CardContent className="pt-6 pb-5">
        <div className="flex items-center justify-between">
          <div className="flex-1">
            <p className="text-xs text-slate-500 font-medium mb-1">
              {title}
              {clickable && <span className="ml-1.5 text-[10px] text-slate-400">(click to view)</span>}
            </p>
            <p className="text-2xl font-bold text-slate-900">{value}</p>
            {subtitle && <p className="text-xs text-slate-400 mt-1">{subtitle}</p>}
          </div>
          <div className={`p-3 rounded-lg ${colors[color]}`}>
            {icon}
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Resource Card Component
function ResourceCard({ title, value, subtitle, icon }) {
  const color = value > 80 ? "red" : value > 60 ? "amber" : "green";
  const bgColor = {
    red: "bg-red-500",
    amber: "bg-amber-500",
    green: "bg-green-500"
  };
  const textColor = {
    red: "text-red-700",
    amber: "text-amber-700",
    green: "text-green-700"
  };

  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="pt-6 pb-5">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-slate-100 rounded-lg text-slate-600">
            {icon}
          </div>
          <div className="flex-1">
            <p className="text-sm font-medium text-slate-900">{title}</p>
            {subtitle && <p className="text-xs text-slate-500">{subtitle}</p>}
          </div>
        </div>
        <div className="space-y-2">
          <div className="flex items-center justify-between">
            <span className={`text-2xl font-bold ${textColor[color]}`}>
              {Math.round(value)}%
            </span>
            {value > 80 && (
              <Badge variant="destructive" className="text-xs">High</Badge>
            )}
          </div>
          <div className="overflow-hidden h-2 rounded-full bg-slate-200">
            <div
              style={{ width: `${Math.min(value, 100)}%` }}
              className={`h-full ${bgColor[color]} transition-all duration-300`}
            />
          </div>
        </div>
      </CardContent>
    </Card>
  );
}

// Summary Card Component
function SummaryCard({ title, value }) {
  return (
    <Card className="border-slate-200 shadow-sm">
      <CardContent className="pt-5 pb-4 text-center">
        <p className="text-xs text-slate-500 font-medium mb-1">{title}</p>
        <p className="text-2xl font-bold text-slate-900">{value.toLocaleString()}</p>
      </CardContent>
    </Card>
  );
}
